import { z } from 'zod';
import { Role, ROLE_VALUES, type Role as RoleT } from './roles';

const roleSchema = z.enum(ROLE_VALUES as [RoleT, ...RoleT[]]);

/** Stored in `account_settings.theme.primary`; API accepts #RRGGBB. */
export const primaryColorHexSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Expected #RRGGBB');

export const userDtoSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  emailVerified: z.boolean(),
  twoFactorEnabled: z.boolean(),
  isSuperAdmin: z.boolean(),
  createdAt: z.string().datetime(),
});
export type UserDto = z.infer<typeof userDtoSchema>;

export const accountDtoSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  name: z.string().min(1),
  createdAt: z.string().datetime(),
  logoUrl: z.string().nullable().optional(),
  primaryColor: primaryColorHexSchema.nullable().optional(),
});
export type AccountDto = z.infer<typeof accountDtoSchema>;

export const accountSettingsDtoSchema = z.object({
  accountId: z.string().uuid(),
  logoUrl: z.string().url().nullable(),
  defaultLocale: z.string().min(2),
  availableLocales: z.array(z.string().min(2)),
  timezone: z.string().min(1),
  theme: z.record(z.string(), z.string()).nullable(),
});
export type AccountSettingsDto = z.infer<typeof accountSettingsDtoSchema>;

export const meDtoSchema = z.object({
  user: userDtoSchema,
  memberships: z.array(
    z.object({
      account: accountDtoSchema,
      role: roleSchema,
    }),
  ),
  activeAccount: accountDtoSchema.nullable(),
});
export type MeDto = z.infer<typeof meDtoSchema>;

export const inviteUserInputSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).optional(),
  phone: z.string().regex(/^\+\d{1,2}\d{10}$/).nullable().optional(),
  isSuperAdmin: z.boolean().optional().default(false),
  accountId: z.string().uuid().optional(),
  role: roleSchema.optional(),
});
export type InviteUserInput = z.infer<typeof inviteUserInputSchema>;

export const inviteMemberInputSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).optional(),
  phone: z.string().regex(/^\+\d{1,2}\d{10}$/).nullable().optional(),
  role: roleSchema,
});
export type InviteMemberInput = z.infer<typeof inviteMemberInputSchema>;

export const userStatusSchema = z.enum(['ACTIVE', 'PENDING_VERIFICATION', 'BANNED']);
export type UserStatus = z.infer<typeof userStatusSchema>;

export const adminUserRowDtoSchema = z.object({
  id: z.string(),
  fullName: z.string().nullable(),
  email: z.string().email(),
  phone: z.string().nullable(),
  isSuperAdmin: z.boolean(),
  status: userStatusSchema,
  createdAt: z.string().datetime(),
  /** Present when the row is scoped to a single account (member list). */
  accountRole: roleSchema.optional(),
});
export type AdminUserRowDto = z.infer<typeof adminUserRowDtoSchema>;

const phoneE164Nullable = z.string().regex(/^\+\d{1,2}\d{10}$/).nullable();

export const updateAdminUserBodySchema = z.object({
  name: z.string().trim().min(1),
  email: z.string().trim().email(),
  phone: phoneE164Nullable,
  isSuperAdmin: z.boolean().optional(),
  banned: z.boolean().optional(),
  emailVerified: z.boolean().optional(),
  accountId: z.string().uuid().optional(),
  role: roleSchema.optional(),
});
export type UpdateAdminUserBody = z.infer<typeof updateAdminUserBodySchema>;

export const updateMemberUserBodySchema = z.object({
  name: z.string().trim().min(1),
  email: z.string().trim().email(),
  phone: phoneE164Nullable,
  role: roleSchema,
});
export type UpdateMemberUserBody = z.infer<typeof updateMemberUserBodySchema>;

export const accountListItemDtoSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  name: z.string().min(1),
  memberCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  logoUrl: z.string().nullable().optional(),
  primaryColor: primaryColorHexSchema.nullable().optional(),
});
export type AccountListItemDto = z.infer<typeof accountListItemDtoSchema>;

const accountMemberInputSchema = z.object({
  userId: z.string().min(1),
  role: roleSchema,
});

export const createAccountBodySchema = z
  .object({
    name: z.string().trim().min(1),
    slug: z.string().trim().min(1).max(64),
    logoUrl: z.union([z.string().url(), z.literal('')]).optional(),
    primaryColor: primaryColorHexSchema.optional(),
    members: z.array(accountMemberInputSchema).max(500).default([]),
  })
  .superRefine((data, ctx) => {
    if (data.members.length > 0) {
      const hasAdmin = data.members.some((m) => m.role === Role.ACCOUNT_ADMIN);
      if (!hasAdmin) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'When adding members, at least one must have the Admin role.',
          path: ['members'],
        });
      }
    }
  });
export type CreateAccountBody = z.infer<typeof createAccountBodySchema>;

export const updateAccountBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    slug: z.string().trim().min(1).max(64).optional(),
    logoUrl: z.union([z.string().url(), z.literal('')]).nullable().optional(),
    primaryColor: primaryColorHexSchema.nullable().optional(),
    members: z.array(accountMemberInputSchema).max(500).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.members !== undefined && data.members.length > 0) {
      const hasAdmin = data.members.some((m) => m.role === Role.ACCOUNT_ADMIN);
      if (!hasAdmin) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'When setting members, at least one must have the Admin role.',
          path: ['members'],
        });
      }
    }
  });
export type UpdateAccountBody = z.infer<typeof updateAccountBodySchema>;

export const accountMemberRowDtoSchema = z.object({
  userId: z.string().min(1),
  email: z.string().email(),
  fullName: z.string().nullable(),
  role: roleSchema,
});
export type AccountMemberRowDto = z.infer<typeof accountMemberRowDtoSchema>;

export const accountDetailDtoSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  name: z.string().min(1),
  createdAt: z.string().datetime(),
  logoUrl: z.string().nullable(),
  primaryColor: primaryColorHexSchema.nullable(),
  members: z.array(accountMemberRowDtoSchema),
});
export type AccountDetailDto = z.infer<typeof accountDetailDtoSchema>;

export const setPasswordInputSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type SetPasswordInput = z.infer<typeof setPasswordInputSchema>;

/** Matches DB `monitor_type`; only HTTP and KEYWORD are creatable via API today. */
export const monitorTypeSchema = z.enum(['HTTP', 'KEYWORD', 'PING', 'PORT']);
export type MonitorType = z.infer<typeof monitorTypeSchema>;

export const monitorKeywordMatchModeSchema = z.enum(['START_WHEN_EXISTS', 'START_WHEN_ABSENT']);
export type MonitorKeywordMatchMode = z.infer<typeof monitorKeywordMatchModeSchema>;

export const monitorStatusSchema = z.enum(['ACTIVE', 'PAUSED']);
export type MonitorStatus = z.infer<typeof monitorStatusSchema>;

export const monitorRuntimeStatusSchema = z.enum(['UP', 'DOWN', 'DEGRADED', 'PENDING']);
export type MonitorRuntimeStatus = z.infer<typeof monitorRuntimeStatusSchema>;

export const monitorIncidentCauseSchema = z.enum([
  'DOWN',
  'SLOW',
  'SSL_ERROR',
  'SSL_EXPIRING',
  'DOMAIN_EXPIRING',
]);
export type MonitorIncidentCause = z.infer<typeof monitorIncidentCauseSchema>;

export const monitorIpVersionSchema = z.enum(['IPV4_FIRST', 'IPV6_FIRST', 'IPV4_ONLY', 'IPV6_ONLY']);
export type MonitorIpVersion = z.infer<typeof monitorIpVersionSchema>;

export const monitorUpStatusCodeSchema = z
  .string()
  .trim()
  .regex(/^([1-5]xx|[1-5][0-9]{2})$/, 'Expected 1xx..5xx or 3-digit code');

export const httpMonitorConfigSchema = z.object({
  url: z.string().trim().url(),
  requestTimeoutSeconds: z.number().int().min(1).max(60),
  followRedirects: z.boolean(),
  ipVersion: monitorIpVersionSchema,
  upStatusCodes: z.array(monitorUpStatusCodeSchema).min(1),
  slowResponseThresholdMs: z.number().int().min(1).max(60000).nullable(),
  sslCheckErrors: z.boolean(),
  sslExpiryReminders: z.boolean(),
  domainExpiryReminders: z.boolean(),
});
export type HttpMonitorConfig = z.infer<typeof httpMonitorConfigSchema>;

export const keywordMonitorConfigSchema = httpMonitorConfigSchema.extend({
  keyword: z.string().trim().min(1).max(1024),
  keywordMatchMode: monitorKeywordMatchModeSchema,
  keywordCaseSensitive: z.boolean(),
});
export type KeywordMonitorConfig = z.infer<typeof keywordMonitorConfigSchema>;

/** Hostname or IPv4/IPv6 literal; validation deferred to probe-time DNS/ICMP. */
export const hostStringSchema = z.string().trim().min(1).max(253);

export const pingMonitorConfigSchema = z.object({
  host: hostStringSchema,
  requestTimeoutSeconds: z.number().int().min(1).max(60),
  slowResponseThresholdMs: z.number().int().min(1).max(60000).nullable().optional(),
});
export type PingMonitorConfig = z.infer<typeof pingMonitorConfigSchema>;

export const portNumberSchema = z.number().int().min(1).max(65535);

export const portMonitorConfigSchema = z.object({
  host: hostStringSchema,
  port: portNumberSchema,
  requestTimeoutSeconds: z.number().int().min(1).max(60),
  slowResponseThresholdMs: z.number().int().min(1).max(60000).nullable().optional(),
});
export type PortMonitorConfig = z.infer<typeof portMonitorConfigSchema>;

const monitorBaseSchema = z.object({
  name: z.string().trim().min(1).max(255),
  tags: z.array(z.string().trim().min(1).max(64)).max(50).default([]),
  intervalSeconds: z.number().int().min(30).max(86400),
  notifyEmailEnabled: z.boolean().default(true),
  notifyEmails: z.array(z.string().email()).max(50).default([]),
});

export const createMonitorBodySchema = z.discriminatedUnion('type', [
  monitorBaseSchema.extend({
    type: z.literal('HTTP'),
    config: httpMonitorConfigSchema,
  }),
  monitorBaseSchema.extend({
    type: z.literal('KEYWORD'),
    config: keywordMonitorConfigSchema,
  }),
  monitorBaseSchema.extend({
    type: z.literal('PING'),
    config: pingMonitorConfigSchema,
  }),
  monitorBaseSchema.extend({
    type: z.literal('PORT'),
    config: portMonitorConfigSchema,
  }),
]);
export type CreateMonitorBody = z.infer<typeof createMonitorBodySchema>;

const monitorBasePatchSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  tags: z.array(z.string().trim().min(1).max(64)).max(50).optional(),
  intervalSeconds: z.number().int().min(30).max(86400).optional(),
  notifyEmailEnabled: z.boolean().optional(),
  notifyEmails: z.array(z.string().email()).max(50).optional(),
});

/** PATCH clients often send '' for unused fields; treat as omitted. */
const emptyHostToUndefined = (v: unknown) =>
  typeof v === 'string' && v.trim() === '' ? undefined : v;

const monitorConfigPatchSchema = httpMonitorConfigSchema
  .partial()
  .extend({
    keyword: z.string().trim().min(1).max(1024).optional(),
    keywordMatchMode: monitorKeywordMatchModeSchema.optional(),
    keywordCaseSensitive: z.boolean().optional(),
    host: z.preprocess(emptyHostToUndefined, hostStringSchema.optional()),
    port: portNumberSchema.optional(),
  })
  .extend({
    url: z.string().trim().url().optional(),
  });

export const updateMonitorBodySchema = monitorBasePatchSchema.extend({
  type: z
    .union([z.literal('HTTP'), z.literal('KEYWORD'), z.literal('PING'), z.literal('PORT')])
    .optional(),
  config: monitorConfigPatchSchema.optional(),
});
export type UpdateMonitorBody = z.infer<typeof updateMonitorBodySchema>;

export const monitorSparkSegmentSchema = z.enum(['up', 'down', 'degraded']);
export type MonitorSparkSegment = z.infer<typeof monitorSparkSegmentSchema>;

/** One bar in the monitor uptime strip: health at a specific check time. */
export const monitorSparkBarDtoSchema = z.object({
  segment: monitorSparkSegmentSchema,
  at: z.string().datetime(),
});
export type MonitorSparkBarDto = z.infer<typeof monitorSparkBarDtoSchema>;

export const monitorListItemDtoSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  name: z.string(),
  type: monitorTypeSchema,
  status: monitorStatusSchema,
  url: z.string().nullable(),
  host: z.string().nullable(),
  port: z.number().int().nullable(),
  tags: z.array(z.string()),
  intervalSeconds: z.number().int(),
  runtimeStatus: monitorRuntimeStatusSchema,
  uptime24hPct: z.number().min(0).max(100).nullable(),
  lastCheckedAt: z.string().datetime().nullable(),
  lastLatencyMs: z.number().int().nullable(),
  lastStatusCode: z.number().int().nullable(),
  lastMessage: z.string().nullable(),
  sparkline: z.array(monitorSparkBarDtoSchema).max(120),
});
export type MonitorListItemDto = z.infer<typeof monitorListItemDtoSchema>;

export const monitorAggregateDtoSchema = z.object({
  down: z.number().int().nonnegative(),
  up: z.number().int().nonnegative(),
  paused: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  uptime24hPct: z.number().min(0).max(100).nullable(),
  mtbfHours24h: z.number().nonnegative().nullable(),
  incidents24h: z.number().int().nonnegative(),
});
export type MonitorAggregateDto = z.infer<typeof monitorAggregateDtoSchema>;

export const monitorSeriesPointDtoSchema = z.object({
  bucketStart: z.string().datetime(),
  avgLatencyMs: z.number().nullable(),
  okRatio: z.number().min(0).max(1).nullable(),
});
export type MonitorSeriesPointDto = z.infer<typeof monitorSeriesPointDtoSchema>;

export const monitorChecksRowDtoSchema = z.object({
  monitorId: z.string().uuid(),
  accountId: z.string().uuid(),
  ts: z.string().datetime(),
  ok: z.boolean(),
  statusCode: z.number().int().nullable(),
  latencyMs: z.number().int().nullable(),
  message: z.string().nullable(),
});
export type MonitorChecksRowDto = z.infer<typeof monitorChecksRowDtoSchema>;

export const monitorStatWindowDtoSchema = z.object({
  range: z.enum(['24h', '7d', '30d', '365d']),
  uptimePct: z.number().min(0).max(100).nullable(),
  /** Incidents with cause DOWN whose `startedAt` falls in the window. */
  downIncidentCount: z.number().int().nonnegative(),
  /** Slow-response (degraded) incidents (`SLOW`) whose `startedAt` falls in the window. */
  slowIncidentCount: z.number().int().nonnegative(),
  /** @deprecated Prefer downIncidentCount + slowIncidentCount; equals their sum. */
  incidentCount: z.number().int().nonnegative(),
  downSeconds: z.number().int().nonnegative(),
});
export type MonitorStatWindowDto = z.infer<typeof monitorStatWindowDtoSchema>;

/** Stats for an arbitrary [from, to] window (same counters as a preset window). */
export const monitorCustomStatDtoSchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
  uptimePct: z.number().min(0).max(100).nullable(),
  downIncidentCount: z.number().int().nonnegative(),
  slowIncidentCount: z.number().int().nonnegative(),
  incidentCount: z.number().int().nonnegative(),
  downSeconds: z.number().int().nonnegative(),
});
export type MonitorCustomStatDto = z.infer<typeof monitorCustomStatDtoSchema>;

export const monitorStatsDtoSchema = z.object({
  windows: z.array(monitorStatWindowDtoSchema),
  mtbfHours: z.number().nonnegative().nullable(),
  /** Present when `from` and `to` query params were sent; otherwise null. */
  custom: monitorCustomStatDtoSchema.nullable(),
});
export type MonitorStatsDto = z.infer<typeof monitorStatsDtoSchema>;

export const monitorIncidentRowDtoSchema = z.object({
  id: z.string().uuid(),
  monitorId: z.string().uuid(),
  cause: monitorIncidentCauseSchema,
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
  lastStatusCode: z.number().int().nullable(),
  lastMessage: z.string().nullable(),
});
export type MonitorIncidentRowDto = z.infer<typeof monitorIncidentRowDtoSchema>;

export const monitorDetailDtoSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  name: z.string(),
  type: monitorTypeSchema,
  status: monitorStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  tags: z.array(z.string()),
  intervalSeconds: z.number().int(),
  notifyEmailEnabled: z.boolean(),
  notifyEmails: z.array(z.string().email()),
  runtimeStatus: monitorRuntimeStatusSchema,
  lastStatusChangedAt: z.string().datetime().nullable(),
  lastCheckedAt: z.string().datetime().nullable(),
  lastLatencyMs: z.number().int().nullable(),
  lastStatusCode: z.number().int().nullable(),
  lastMessage: z.string().nullable(),
  http: httpMonitorConfigSchema
    .extend({
      upStatusCodes: z.array(z.string()),
      keyword: z.string().nullable(),
      keywordMatchMode: monitorKeywordMatchModeSchema,
      keywordCaseSensitive: z.boolean(),
    })
    .nullable(),
  ping: z
    .object({
      host: hostStringSchema,
      requestTimeoutSeconds: z.number().int(),
      slowResponseThresholdMs: z.number().int().nullable(),
    })
    .nullable(),
  port: z
    .object({
      host: hostStringSchema,
      port: portNumberSchema,
      requestTimeoutSeconds: z.number().int(),
      slowResponseThresholdMs: z.number().int().nullable(),
    })
    .nullable(),
  sslCertExpiresAt: z.string().datetime().nullable(),
  sslLastCheckedAt: z.string().datetime().nullable(),
  sslLastError: z.string().nullable(),
  domainExpiresAt: z.string().datetime().nullable(),
  domainLastCheckedAt: z.string().datetime().nullable(),
  domainLastError: z.string().nullable(),
});
export type MonitorDetailDto = z.infer<typeof monitorDetailDtoSchema>;

// Incident schemas
export const incidentVisibilitySchema = z.enum(['INCLUDED', 'EXCLUDED']);
export type IncidentVisibility = z.infer<typeof incidentVisibilitySchema>;

export const incidentRuntimeStatusSchema = z.enum(['ONGOING', 'RESOLVED', 'ACKNOWLEDGED']);
export type IncidentRuntimeStatus = z.infer<typeof incidentRuntimeStatusSchema>;

export const incidentEventKindSchema = z.enum([
  'DETECTED',
  'RESOLVED',
  'NOTIFICATION_SENT',
  'NOTIFICATION_FAILED',
  'ACKNOWLEDGED',
  'COMMENT_ADDED',
  'VISIBILITY_CHANGED',
]);
export type IncidentEventKind = z.infer<typeof incidentEventKindSchema>;

export const accountIncidentRowDtoSchema = z.object({
  id: z.string().uuid(),
  monitorId: z.string().uuid(),
  monitorName: z.string(),
  monitorUrl: z.string().nullable(),
  monitorHost: z.string().nullable(),
  monitorPort: z.number().int().nullable(),
  monitorTags: z.array(z.string()),
  cause: monitorIncidentCauseSchema,
  status: incidentRuntimeStatusSchema,
  visibility: incidentVisibilitySchema,
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
  acknowledgedAt: z.string().datetime().nullable(),
  durationSeconds: z.number().int().nullable(),
  commentCount: z.number().int().nonnegative(),
  lastStatusCode: z.number().int().nullable(),
  lastMessage: z.string().nullable(),
});
export type AccountIncidentRowDto = z.infer<typeof accountIncidentRowDtoSchema>;

export const accountIncidentDetailDtoSchema = accountIncidentRowDtoSchema.extend({
  httpRequest: z.object({
    method: z.string(),
    url: z.string(),
    headers: z.record(z.string(), z.string()).nullable(),
  }).nullable(),
  httpResponse: z.object({
    statusCode: z.number().int().nullable(),
    body: z.string().nullable(),
    headers: z.record(z.string(), z.string()).nullable(),
  }).nullable(),
});
export type AccountIncidentDetailDto = z.infer<typeof accountIncidentDetailDtoSchema>;

export const incidentEventDtoSchema = z.object({
  id: z.string().uuid(),
  incidentId: z.string().uuid(),
  kind: incidentEventKindSchema,
  actorUserId: z.string().nullable(),
  actorUserName: z.string().nullable(),
  message: z.string(),
  payload: z.record(z.unknown()).nullable(),
  createdAt: z.string().datetime(),
});
export type IncidentEventDto = z.infer<typeof incidentEventDtoSchema>;

export const incidentCommentDtoSchema = z.object({
  id: z.string().uuid(),
  incidentId: z.string().uuid(),
  authorUserId: z.string(),
  authorUserName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type IncidentCommentDto = z.infer<typeof incidentCommentDtoSchema>;

export const addIncidentCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(5000),
});
export type AddIncidentCommentBody = z.infer<typeof addIncidentCommentBodySchema>;

export const updateIncidentVisibilityBodySchema = z.object({
  visibility: incidentVisibilitySchema,
});
export type UpdateIncidentVisibilityBody = z.infer<typeof updateIncidentVisibilityBodySchema>;

export const acknowledgeIncidentBodySchema = z.object({});
export type AcknowledgeIncidentBody = z.infer<typeof acknowledgeIncidentBodySchema>;

// ---------------------------------------------------------------------------
// Maintenance windows
// ---------------------------------------------------------------------------

export const maintenanceScheduleKindSchema = z.enum(['DAILY', 'WEEKLY', 'MONTHLY', 'ONE_OFF']);
export type MaintenanceScheduleKind = z.infer<typeof maintenanceScheduleKindSchema>;

export const maintenanceWindowStatusSchema = z.enum(['ACTIVE', 'PAUSED']);
export type MaintenanceWindowStatus = z.infer<typeof maintenanceWindowStatusSchema>;

const maintenanceWindowBaseSchema = z.object({
  name: z.string().trim().min(1).max(255),
  durationMinutes: z.number().int().min(1).max(1440),
  monitorIds: z.array(z.string().uuid()).max(500).default([]),
});

const startMinuteSchema = z.number().int().min(0).max(1439);

export const createMaintenanceWindowBodySchema = z.discriminatedUnion('scheduleKind', [
  maintenanceWindowBaseSchema.extend({
    scheduleKind: z.literal('DAILY'),
    startMinute: startMinuteSchema,
  }),
  maintenanceWindowBaseSchema.extend({
    scheduleKind: z.literal('WEEKLY'),
    startMinute: startMinuteSchema,
    daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1).max(7),
  }),
  maintenanceWindowBaseSchema.extend({
    scheduleKind: z.literal('MONTHLY'),
    startMinute: startMinuteSchema,
    daysOfMonth: z.array(z.number().int().min(1).max(31)).min(1),
  }),
  maintenanceWindowBaseSchema.extend({
    scheduleKind: z.literal('ONE_OFF'),
    startsAt: z.string().datetime(),
  }),
]);
export type CreateMaintenanceWindowBody = z.infer<typeof createMaintenanceWindowBodySchema>;

export const updateMaintenanceWindowBodySchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  scheduleKind: maintenanceScheduleKindSchema.optional(),
  startMinute: startMinuteSchema.optional(),
  durationMinutes: z.number().int().min(1).max(1440).optional(),
  daysOfWeek: z.array(z.number().int().min(0).max(6)).max(7).optional(),
  daysOfMonth: z.array(z.number().int().min(1).max(31)).optional(),
  startsAt: z.string().datetime().nullable().optional(),
  monitorIds: z.array(z.string().uuid()).max(500).optional(),
});
export type UpdateMaintenanceWindowBody = z.infer<typeof updateMaintenanceWindowBodySchema>;

export const maintenanceWindowDtoSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  name: z.string(),
  status: maintenanceWindowStatusSchema,
  scheduleKind: maintenanceScheduleKindSchema,
  startMinute: z.number().int(),
  durationMinutes: z.number().int(),
  daysOfWeek: z.array(z.number().int()),
  daysOfMonth: z.array(z.number().int()),
  startsAt: z.string().datetime().nullable(),
  timezone: z.string(),
  monitorIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MaintenanceWindowDto = z.infer<typeof maintenanceWindowDtoSchema>;

// ---------------------------------------------------------------------------
// Status pages
// ---------------------------------------------------------------------------

export const statusPageAccessSchema = z.enum(['PUBLIC', 'PASSWORD']);
export type StatusPageAccess = z.infer<typeof statusPageAccessSchema>;

export const statusPageMonitorSelectionSchema = z.enum(['ALL', 'SUBSET']);
export type StatusPageMonitorSelection = z.infer<typeof statusPageMonitorSelectionSchema>;

export const statusPageSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug must be lowercase letters, numbers, and dashes');

export const createStatusPageBodySchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    slug: statusPageSlugSchema,
    access: statusPageAccessSchema.default('PUBLIC'),
    password: z.string().min(4).max(255).optional(),
    monitorSelection: statusPageMonitorSelectionSchema.default('ALL'),
    monitorIds: z.array(z.string().uuid()).max(500).default([]),
    published: z.boolean().default(true),
  })
  .refine((v) => v.access !== 'PASSWORD' || (v.password && v.password.length >= 4), {
    message: 'Password is required when access is PASSWORD',
    path: ['password'],
  });
export type CreateStatusPageBody = z.infer<typeof createStatusPageBodySchema>;

export const updateStatusPageBodySchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  slug: statusPageSlugSchema.optional(),
  access: statusPageAccessSchema.optional(),
  password: z.string().min(4).max(255).nullable().optional(),
  monitorSelection: statusPageMonitorSelectionSchema.optional(),
  monitorIds: z.array(z.string().uuid()).max(500).optional(),
  published: z.boolean().optional(),
});
export type UpdateStatusPageBody = z.infer<typeof updateStatusPageBodySchema>;

export const statusPageDtoSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  access: statusPageAccessSchema,
  hasPassword: z.boolean(),
  monitorSelection: statusPageMonitorSelectionSchema,
  monitorIds: z.array(z.string().uuid()),
  published: z.boolean(),
  publicUrlPath: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type StatusPageDto = z.infer<typeof statusPageDtoSchema>;

// ---------------------------------------------------------------------------
// Admin overview (super-admin cross-account dashboard)
// ---------------------------------------------------------------------------

export const adminOverviewActiveIncidentDtoSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  accountName: z.string(),
  accountSlug: z.string(),
  monitorName: z.string(),
  monitorHost: z.string().nullable(),
  monitorUrl: z.string().nullable(),
  cause: monitorIncidentCauseSchema,
  status: incidentRuntimeStatusSchema,
  startedAt: z.string().datetime(),
  durationSeconds: z.number().int().nullable(),
});
export type AdminOverviewActiveIncidentDto = z.infer<typeof adminOverviewActiveIncidentDtoSchema>;

export const adminOverviewAccountRowDtoSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  memberCount: z.number().int().nonnegative(),
  monitorCount: z.number().int().nonnegative(),
  up: z.number().int().nonnegative(),
  down: z.number().int().nonnegative(),
  degraded: z.number().int().nonnegative(),
  paused: z.number().int().nonnegative(),
  activeIncidents: z.number().int().nonnegative(),
});
export type AdminOverviewAccountRowDto = z.infer<typeof adminOverviewAccountRowDtoSchema>;

export const adminOverviewDtoSchema = z.object({
  totals: z.object({
    accounts: z.number().int().nonnegative(),
    monitors: z.number().int().nonnegative(),
    up: z.number().int().nonnegative(),
    down: z.number().int().nonnegative(),
    degraded: z.number().int().nonnegative(),
    paused: z.number().int().nonnegative(),
    activeIncidents: z.number().int().nonnegative(),
    incidents24h: z.number().int().nonnegative(),
  }),
  uptime24hPct: z.number().min(0).max(100).nullable(),
  activeIncidents: z.array(adminOverviewActiveIncidentDtoSchema),
  accounts: z.array(adminOverviewAccountRowDtoSchema),
});
export type AdminOverviewDto = z.infer<typeof adminOverviewDtoSchema>;

export const verifyStatusPagePasswordBodySchema = z.object({
  password: z.string().min(1).max(255),
});
export type VerifyStatusPagePasswordBody = z.infer<typeof verifyStatusPagePasswordBodySchema>;

export const publicStatusMonitorDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  url: z.string().nullable(),
  host: z.string().nullable(),
  port: z.number().int().nullable(),
  type: monitorTypeSchema,
  status: monitorStatusSchema,
  runtimeStatus: monitorRuntimeStatusSchema,
  uptime24hPct: z.number().min(0).max(100).nullable(),
  sparkline: z.array(monitorSparkBarDtoSchema),
});
export type PublicStatusMonitorDto = z.infer<typeof publicStatusMonitorDtoSchema>;

// Aggregate uptime across the whole status page, per time window.
export const publicStatusUptimeWindowSchema = z.object({
  window: z.enum(['24h', '7d', '30d', '90d']),
  uptimePct: z.number().min(0).max(100).nullable(),
});
export type PublicStatusUptimeWindowDto = z.infer<typeof publicStatusUptimeWindowSchema>;

// One status-update feed item, auto-derived from an incident or maintenance window.
export const publicStatusUpdateDtoSchema = z.object({
  id: z.string(),
  kind: z.enum(['INCIDENT', 'MAINTENANCE', 'ANNOUNCEMENT']),
  title: z.string(),
  monitorName: z.string().nullable(),
  /** INCIDENT: 'ONGOING' | 'RESOLVED'; MAINTENANCE: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED'; ANNOUNCEMENT: 'PUBLISHED' */
  state: z.string(),
  cause: z.string().nullable(),
  message: z.string().nullable(),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
});
export type PublicStatusUpdateDto = z.infer<typeof publicStatusUpdateDtoSchema>;

export const publicStatusPageDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
  accountSlug: z.string(),
  accountName: z.string(),
  logoUrl: z.string().nullable(),
  access: statusPageAccessSchema,
  /** When access is PASSWORD and the caller has not yet verified. */
  locked: z.boolean(),
  updatedAt: z.string().datetime(),
  monitors: z.array(publicStatusMonitorDtoSchema),
  overallUptime: z.array(publicStatusUptimeWindowSchema),
  /** Updates posted in the last 30 days (incidents + maintenance). */
  recentUpdates: z.array(publicStatusUpdateDtoSchema),
  /** Whether to show the "All messages" filter button on the status page. */
  showAllMessagesFilter: z.boolean().optional(),
});
export type PublicStatusPageDto = z.infer<typeof publicStatusPageDtoSchema>;

export const publicStatusUpdatesDtoSchema = z.object({
  updates: z.array(publicStatusUpdateDtoSchema),
});
export type PublicStatusUpdatesDto = z.infer<typeof publicStatusUpdatesDtoSchema>;

// --- Status page subscriptions ---------------------------------------------

// Public: subscribe / unsubscribe by email.
export const subscribeStatusPageBodySchema = z.object({
  email: z.string().email().max(320),
});
export type SubscribeStatusPageBody = z.infer<typeof subscribeStatusPageBodySchema>;

export const unsubscribeStatusPageBodySchema = subscribeStatusPageBodySchema;
export type UnsubscribeStatusPageBody = z.infer<typeof unsubscribeStatusPageBodySchema>;

// Public: check whether a single email is subscribed (so a visitor can manage
// their own subscription without exposing anyone else's address).
export const subscriptionStatusBodySchema = subscribeStatusPageBodySchema;
export type SubscriptionStatusBody = z.infer<typeof subscriptionStatusBodySchema>;

export const publicSubscriptionStatusDtoSchema = z.object({
  email: z.string(),
  subscribed: z.boolean(),
});
export type PublicSubscriptionStatusDto = z.infer<typeof publicSubscriptionStatusDtoSchema>;

// Public: the full subscriber list shown in the status-page "Manage" popover.
export const publicStatusSubscribersDtoSchema = z.object({
  subscribers: z.array(z.object({ email: z.string() })),
});
export type PublicStatusSubscribersDto = z.infer<typeof publicStatusSubscribersDtoSchema>;

// Admin: list / add subscribers from the dashboard.
export const statusPageSubscriberDtoSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  createdAt: z.string().datetime(),
});
export type StatusPageSubscriberDto = z.infer<typeof statusPageSubscriberDtoSchema>;

export const statusPageSubscribersDtoSchema = z.object({
  subscribers: z.array(statusPageSubscriberDtoSchema),
});
export type StatusPageSubscribersDto = z.infer<typeof statusPageSubscribersDtoSchema>;

export const addStatusPageSubscriberBodySchema = subscribeStatusPageBodySchema;
export type AddStatusPageSubscriberBody = z.infer<typeof addStatusPageSubscriberBodySchema>;

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

export const announcementDtoSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  statusPageId: z.string().uuid().nullable(),
  title: z.string(),
  content: z.string(),
  published: z.boolean(),
  publishedAt: z.string().datetime().nullable(),
  autoHide: z.boolean(),
  autoHideAt: z.string().datetime().nullable(),
  notifySubscribers: z.boolean(),
  notifiedAt: z.string().datetime().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AnnouncementDto = z.infer<typeof announcementDtoSchema>;

export const createAnnouncementBodySchema = z.object({
  title: z.string().trim().min(1).max(255),
  content: z.string().trim().min(1).max(10000),
  statusPageId: z.string().uuid().nullable().optional(),
  published: z.boolean().default(false),
  publishedAt: z.string().datetime().nullable().optional(),
  autoHide: z.boolean().default(false),
  autoHideDurationMinutes: z.number().int().min(1).max(525600).nullable().optional(),
  notifySubscribers: z.boolean().default(false),
});
export type CreateAnnouncementBody = z.infer<typeof createAnnouncementBodySchema>;

export const updateAnnouncementBodySchema = z.object({
  title: z.string().trim().min(1).max(255).optional(),
  content: z.string().trim().min(1).max(10000).optional(),
  statusPageId: z.string().uuid().nullable().optional(),
  published: z.boolean().optional(),
  publishedAt: z.string().datetime().nullable().optional(),
  autoHide: z.boolean().optional(),
  autoHideDurationMinutes: z.number().int().min(1).max(525600).nullable().optional(),
  notifySubscribers: z.boolean().optional(),
});
export type UpdateAnnouncementBody = z.infer<typeof updateAnnouncementBodySchema>;

// One day of a monitor's uptime history, bucketed in the account timezone.
export const publicStatusDailyDaySchema = z.object({
  date: z.string(), // 'YYYY-MM-DD' in the account timezone
  uptimePct: z.number().min(0).max(100).nullable(),
  okChecks: z.number().int(),
  totalChecks: z.number().int(),
});
export type PublicStatusDailyDayDto = z.infer<typeof publicStatusDailyDaySchema>;

// A monitor's per-day uptime history for the calendar view.
export const publicStatusMonitorDailyDtoSchema = z.object({
  monitorId: z.string().uuid(),
  monitorName: z.string(),
  timezone: z.string(),
  /** 'YYYY-MM-DD' of the first ever check, or null when the monitor has no data. */
  earliestDate: z.string().nullable(),
  /** Only days that have at least one check; gaps are rendered as "no data". */
  days: z.array(publicStatusDailyDaySchema),
});
export type PublicStatusMonitorDailyDto = z.infer<typeof publicStatusMonitorDailyDtoSchema>;

// ---------------------------------------------------------------------------
// Global search (command palette)
// ---------------------------------------------------------------------------

export const searchResultTypeSchema = z.enum([
  'account',
  'user',
  'monitor',
  'status_page',
  'maintenance',
]);
export type SearchResultType = z.infer<typeof searchResultTypeSchema>;

export const searchResultDtoSchema = z.object({
  type: searchResultTypeSchema,
  id: z.string().uuid(),
  label: z.string(),
  description: z.string().optional(),
  accountId: z.string().uuid(),
  accountSlug: z.string(),
  path: z.string(),
});
export type SearchResultDto = z.infer<typeof searchResultDtoSchema>;

export const globalSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(200),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type GlobalSearchQuery = z.infer<typeof globalSearchQuerySchema>;

export const globalSearchResponseSchema = z.object({
  results: z.array(searchResultDtoSchema),
});
export type GlobalSearchResponse = z.infer<typeof globalSearchResponseSchema>;

// ============================================================================
// Audit log (CEF)
// ============================================================================

export const auditSeverityLevelSchema = z.enum(['critical', 'high', 'medium', 'low']);
export type AuditSeverityLevel = z.infer<typeof auditSeverityLevelSchema>;

export const auditOutcomeSchema = z.enum(['success', 'failure']);
export type AuditOutcome = z.infer<typeof auditOutcomeSchema>;

export const auditCategorySchema = z.enum([
  'auth',
  'monitor',
  'user',
  'account',
  'alert',
  'status_page',
  'maintenance',
  'settings',
]);

export const auditEventDtoSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string().datetime(),
  signatureId: z.number().int(),
  eventName: z.string(),
  severity: z.number().int().min(0).max(10),
  severityLevel: auditSeverityLevelSchema,
  category: auditCategorySchema,
  outcome: auditOutcomeSchema,
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  entityName: z.string().nullable(),
  userId: z.string().nullable(),
  userEmail: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  changedFields: z.record(z.string(), z.object({ before: z.unknown(), after: z.unknown() })).nullable(),
  rawCefLine: z.string(),
});
export type AuditEventDto = z.infer<typeof auditEventDtoSchema>;

export const auditStatsDtoSchema = z.object({
  total: z.number().int(),
  criticalHigh: z.number().int(),
  uniqueUsers: z.number().int(),
  entitiesAffected: z.number().int(),
});
export type AuditStatsDto = z.infer<typeof auditStatsDtoSchema>;

export const auditSignatureDtoSchema = z.object({
  signatureId: z.number().int(),
  name: z.string(),
  severity: z.number().int().min(0).max(10),
  category: auditCategorySchema,
});
export type AuditSignatureDto = z.infer<typeof auditSignatureDtoSchema>;

export const auditQueryParamsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  sort: z.enum(['createdAt', 'severity', 'eventName']).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
  search: z.string().optional(),
  severityLevel: auditSeverityLevelSchema.optional(),
  severityMin: z.coerce.number().int().min(0).max(10).optional(),
  severityMax: z.coerce.number().int().min(0).max(10).optional(),
  category: auditCategorySchema.optional(),
  entityType: z.string().optional(),
  outcome: auditOutcomeSchema.optional(),
  userId: z.string().optional(),
  entityId: z.string().optional(),
  signatureId: z.coerce.number().int().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});
export type AuditQueryParams = z.infer<typeof auditQueryParamsSchema>;

export const auditExportFormatSchema = z.enum(['cef', 'csv', 'json']);
export type AuditExportFormat = z.infer<typeof auditExportFormatSchema>;
